import { RemoteConfigProperties, ViewId } from "@madhive/mad-sdk";
import { selectProductionViews } from "appReducers/authReducer";
import { Routes } from "lib/constants/routes";

/** Feature flags the main menu reads, keyed by the name rules use. */
export const MAIN_MENU_FLAGS = {
  fiveMinSeparation: RemoteConfigProperties.INTERNAL_FIVE_MIN_SEPARATION,
  orgSettings: RemoteConfigProperties.ORG_SETTINGS,
  publisherManager: RemoteConfigProperties.PUBLISHER_MANAGER,
  audienceDataProvisioning:
    RemoteConfigProperties.ENABLE_AUDIENCE_DATA_PROVISIONING,
  dmpDashboard: RemoteConfigProperties.AUDIENCES_DMP_DASHBOARD,
  supplyTransparency: RemoteConfigProperties.SUPPLY_SELECTOR_PRODUCT_VIEW,
  publisherGroupsV2: RemoteConfigProperties.PUBLISHER_GROUPS_V2,
  iasKeywordTargeting: RemoteConfigProperties.IAS_KEYWORD_TARGETING,
  directUpload: RemoteConfigProperties.DIRECT_1P_FILE_UPLOAD,
  maverickExternal: RemoteConfigProperties.MAVERICK_AI_SEGMENTS_EXTERNAL_UI,
  maverickSeller: RemoteConfigProperties.MAVERICK_SELLER,
  aiCreative: RemoteConfigProperties.MAVERICK_AI_CREATIVE,
  promotedDashboardsNav:
    RemoteConfigProperties.PROMOTED_DASHBOARDS_REPORTING_NAV
} as const;

export type ProductionViews = ReturnType<typeof selectProductionViews>;

/** Inputs a main menu rule can read. */
export interface MainMenuContext {
  /** Views assigned to the user, before any rule runs. */
  assignedViews: ReadonlySet<ViewId>;
  orgProductionViews: ProductionViews;
  isDev: boolean;
  isRootUser: boolean;
  isMadhiveAdmin: boolean;
  canViewManageDeals: boolean;
  canViewManagePrices: boolean;
  flags: Record<keyof typeof MAIN_MENU_FLAGS, boolean>;
}

/** Without a rule, a view shows when assigned and in the org's production views. */
export interface MainMenuViewRule {
  viewId: ViewId;
  /** Adds the view even when it isn't assigned to the user. */
  inject?: (ctx: MainMenuContext) => boolean;
  /** Replaces the default org production check. */
  shouldRender?: (ctx: MainMenuContext, isOrgView: boolean) => boolean;
  /** Adds the view for dev users after every `shouldRender` has run. */
  includeForDev?: (ctx: MainMenuContext) => boolean;
}

export interface InternalToolView {
  name: string;
  route: Routes;
  /** Defaults to showing for every dev user. */
  shouldRender?: (ctx: MainMenuContext) => boolean;
}

const isOrgViewInProduction = (ctx: MainMenuContext, viewId: ViewId) =>
  !!ctx.orgProductionViews[viewId as keyof ProductionViews]?.production;

// Requires both org registration and the user's own grant, so the link never
// points at a page AuthorizedOnly then rejects.
const canSeeAiCreative = (ctx: MainMenuContext) =>
  isOrgViewInProduction(ctx, ViewId.MAVERICK_AI_CREATIVE) &&
  ctx.flags.aiCreative;

export const MAIN_MENU_VIEW_RULES: readonly MainMenuViewRule[] = [
  {
    viewId: ViewId.MANAGE_PUBLISHER_GROUPS,
    shouldRender: (ctx, isOrgView) => ctx.flags.publisherGroupsV2 && isOrgView
  },
  {
    viewId: ViewId.MANAGE_PRODUCTS,
    shouldRender: (ctx, isOrgView) => ctx.flags.supplyTransparency || isOrgView,
    includeForDev: () => true
  },
  {
    viewId: ViewId.PUBLISHER_MANAGER,
    shouldRender: (ctx, isOrgView) => ctx.flags.publisherManager || isOrgView,
    includeForDev: ctx => ctx.flags.publisherManager
  },
  {
    viewId: ViewId.MANAGE_PRICES,
    shouldRender: ctx => ctx.canViewManagePrices,
    includeForDev: ctx => ctx.canViewManagePrices
  },
  {
    viewId: ViewId.IAS_KEYWORDS_UPLOADER,
    shouldRender: (ctx, isOrgView) => ctx.flags.iasKeywordTargeting || isOrgView
  },
  {
    viewId: ViewId.FIRST_PARTY_DATA_ONBOARDING,
    inject: ctx => ctx.flags.directUpload,
    shouldRender: (ctx, isOrgView) => ctx.flags.directUpload || isOrgView
  },
  {
    viewId: ViewId.MAVERICK_AI_SEGMENTS_EXTERNAL,
    inject: ctx => ctx.flags.maverickExternal,
    shouldRender: (ctx, isOrgView) => ctx.flags.maverickExternal || isOrgView
  },
  {
    viewId: ViewId.MAVERICK_SELLER,
    shouldRender: ctx => ctx.flags.maverickSeller
  },
  {
    viewId: ViewId.MAVERICK_SELLER_CLIENTS,
    inject: ctx => ctx.assignedViews.has(ViewId.MAVERICK_SELLER),
    shouldRender: ctx => ctx.flags.maverickSeller
  },
  {
    viewId: ViewId.MAVERICK_AI_CREATIVE,
    shouldRender: canSeeAiCreative
  },
  {
    viewId: ViewId.MAVERICK_AI_CREATIVE_LIBRARY,
    inject: ctx => ctx.assignedViews.has(ViewId.MAVERICK_AI_CREATIVE),
    shouldRender: canSeeAiCreative
  },
  {
    viewId: ViewId.PROMOTED_DASHBOARDS,
    inject: ctx => ctx.flags.promotedDashboardsNav,
    shouldRender: ctx => ctx.flags.promotedDashboardsNav
  }
];

const hasAssignedView = (ctx: MainMenuContext, viewId: ViewId) =>
  ctx.assignedViews.has(viewId);

/** Dev-only "Internal Tools" links. */
export const INTERNAL_TOOL_VIEWS: readonly InternalToolView[] = [
  { name: "Audiences", route: Routes.MANAGE_AUDIENCES },
  {
    name: "Audience Data Provisioning",
    route: Routes.AUDIENCE_DATA_PROVISIONING,
    shouldRender: ctx =>
      ctx.flags.audienceDataProvisioning && ctx.isMadhiveAdmin
  },
  {
    name: "Deals",
    route: Routes.MANAGE_DEALS,
    shouldRender: ctx => ctx.canViewManageDeals
  },
  { name: "Custom Reporting", route: Routes.CUSTOM_REPORTING },
  { name: "Crypto Dashboard", route: Routes.CRYPTO_DASHBOARD },
  { name: "DataStudio Reports", route: Routes.DATASTUDIO_REPORTS },
  { name: "Advanced Exports", route: Routes.ADVANCED_EXPORTS },
  { name: "Foot Traffic Attribution", route: Routes.FOOT_TRAFFIC_ATTRIBUTION },
  {
    name: "5 Minute Separation",
    route: Routes.FIVE_MIN_SEPARATION,
    shouldRender: ctx => ctx.flags.fiveMinSeparation
  },
  { name: "Conversion Dashboard (BETA)", route: Routes.CONVERSION_DASHBOARD },
  { name: "Spend Dashboard (BETA)", route: Routes.SPEND_DASHBOARD },
  {
    name: "BI Conversion Dashboard (ALPHA)",
    route: Routes.BI_CONVERSION_DASHBOARD
  },
  { name: "Report Builder (BETA)", route: Routes.REPORT_BUILDER },
  { name: "Report Center (BETA)", route: Routes.REPORT_CENTER },
  { name: "Pacing (BETA)", route: Routes.PACING_BETA },
  { name: "Planner Admin Tool", route: Routes.PLANNER_ADMIN_TOOL },
  {
    name: "Org Settings",
    route: Routes.ORG_SETTINGS,
    shouldRender: ctx => ctx.flags.orgSettings
  },
  {
    name: "DMP Dashboard",
    route: Routes.DMP_DASHBOARD,
    shouldRender: ctx => ctx.flags.dmpDashboard
  },
  { name: "Insights Dashboard", route: Routes.INSIGHTS_DASHBOARD },
  {
    name: "Maverick AI Segments",
    route: Routes.MAVERICK_AI_SEGMENTS,
    shouldRender: ctx => ctx.isMadhiveAdmin
  },
  { name: "Manage AI Sessions", route: Routes.MANAGE_AI_AGENTS },
  {
    name: "Experiments",
    route: Routes.MANAGE_EXPERIMENTS,
    shouldRender: ctx => ctx.isRootUser
  },
  {
    name: "Experiment Meta",
    route: Routes.MANAGE_EXPERIMENT_META,
    shouldRender: ctx => ctx.isRootUser
  },
  {
    name: "Proposals",
    route: Routes.MAVERICK_SELLER_PROPOSALS,
    shouldRender: ctx =>
      ctx.flags.maverickSeller && hasAssignedView(ctx, ViewId.MAVERICK_SELLER)
  },
  {
    name: "AI Creative",
    route: Routes.MAVERICK_AI_CREATIVE,
    shouldRender: ctx =>
      ctx.flags.aiCreative && hasAssignedView(ctx, ViewId.MAVERICK_AI_CREATIVE)
  },
  {
    name: "AI Creative Library",
    route: Routes.MAVERICK_AI_CREATIVE_LIBRARY,
    shouldRender: ctx =>
      ctx.flags.aiCreative && hasAssignedView(ctx, ViewId.MAVERICK_AI_CREATIVE)
  },
  { name: "Promoted Dashboards", route: Routes.PROMOTED_DASHBOARDS }
];

/** Main menu views to show, in order. */
export const resolveMainMenuViews = (
  ctx: MainMenuContext,
  rules: readonly MainMenuViewRule[] = MAIN_MENU_VIEW_RULES
): ViewId[] => {
  const rulesByViewId = new Map(rules.map(rule => [rule.viewId, rule]));

  const candidates = new Set(ctx.assignedViews);
  rules.forEach(rule => {
    if (rule.inject?.(ctx)) {
      candidates.add(rule.viewId);
    }
  });

  const visible = [...candidates].filter(viewId => {
    const isOrgView = !!ctx.orgProductionViews[viewId as keyof ProductionViews];
    const rule = rulesByViewId.get(viewId);
    return rule?.shouldRender ? rule.shouldRender(ctx, isOrgView) : isOrgView;
  });

  if (ctx.isDev) {
    rules.forEach(rule => {
      if (rule.includeForDev?.(ctx) && !visible.includes(rule.viewId)) {
        visible.push(rule.viewId);
      }
    });
  }

  return visible;
};

export const resolveInternalTools = (
  ctx: MainMenuContext,
  tools: readonly InternalToolView[] = INTERNAL_TOOL_VIEWS
): InternalToolView[] => tools.filter(tool => tool.shouldRender?.(ctx) ?? true);
